import { useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import { toast } from 'react-toastify';
import { MediaThumb, MediaLightbox } from './MediaGallery';
import {
  ALLOWED_MEDIA_TYPES,
  MAX_MEDIA_SIZE,
  MAX_MEDIA_PER_SECTION,
  mediaKind,
  fileNameFromKey,
} from '../Helpers/format';

let pendingSeq = 0;

/** Wrap browser Files into pending-file records with preview URLs. */
export const toPendingFiles = (files) =>
  files.map((f) => ({
    id: `p${Date.now()}_${pendingSeq++}`,
    file: f,
    name: f.name,
    type: f.type,
    kind: f.type.startsWith('image/') ? 'image' : f.type.startsWith('video/') ? 'video' : mediaKind(f.name),
    preview: f.type.startsWith('image/') || f.type.startsWith('video/') ? URL.createObjectURL(f) : null,
  }));

/**
 * Media dropzone with thumbnails for already-uploaded keys and not-yet-uploaded files.
 *
 * Props
 * - id: string (for aria / input ids)
 * - existingKeys: [s3key]         signedUrls: { [key]: url }
 * - pendingFiles: [{ id, file, name, kind, preview }]
 * - onPendingChange(next)          onRemoveExisting(key) (optional; hides the X when absent)
 * - required: boolean             invalid: boolean (shows red border)
 * - disabled: boolean
 */
const MediaZone = ({
  id,
  existingKeys = [],
  signedUrls = {},
  pendingFiles = [],
  onPendingChange,
  onRemoveExisting,
  required = false,
  invalid = false,
  disabled = false,
}) => {
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });

  const total = existingKeys.length + pendingFiles.length;
  const remaining = Math.max(0, MAX_MEDIA_PER_SECTION - total);
  const atLimit = remaining === 0;

  const addFiles = (accepted, rejected = []) => {
    if (disabled) return;
    rejected.forEach(({ file, errors }) =>
      (errors || []).forEach((e) => {
        if (e.code === 'file-too-large') toast.error(`${file.name} exceeds 120 MB`);
        else if (e.code === 'file-invalid-type') toast.error(`${file.name}: unsupported file type`);
        else toast.error(`${file.name}: ${e.message || 'rejected'}`);
      })
    );
    if (!accepted.length) return;
    const toAdd = accepted.slice(0, remaining);
    if (accepted.length > remaining) toast.warn(`Up to ${MAX_MEDIA_PER_SECTION} files per section`);
    if (!toAdd.length) return;
    onPendingChange([...pendingFiles, ...toPendingFiles(toAdd)]);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: addFiles,
    accept: ALLOWED_MEDIA_TYPES,
    multiple: true,
    maxSize: MAX_MEDIA_SIZE,
    noClick: true,
    noKeyboard: true,
    disabled,
  });

  const handleNativePick = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const ok = [];
    const bad = [];
    const allowed = ALLOWED_MEDIA_TYPES.split(',');
    files.forEach((f) => {
      if (f.size > MAX_MEDIA_SIZE) bad.push({ file: f, errors: [{ code: 'file-too-large' }] });
      else if (f.type && !allowed.includes(f.type)) bad.push({ file: f, errors: [{ code: 'file-invalid-type' }] });
      else ok.push(f);
    });
    addFiles(ok, bad);
  };

  // Revoke object URLs when the component unmounts.
  const pendingRef = useRef(pendingFiles);
  pendingRef.current = pendingFiles;
  useEffect(
    () => () => {
      pendingRef.current.forEach((p) => p.preview && URL.revokeObjectURL(p.preview));
    },
    []
  );

  const removePending = (pid) => {
    const target = pendingFiles.find((p) => p.id === pid);
    if (target?.preview) URL.revokeObjectURL(target.preview);
    onPendingChange(pendingFiles.filter((p) => p.id !== pid));
  };

  // Lightbox items: existing (signed) then pending (object URLs)
  const lightboxItems = [
    ...existingKeys.map((k) => ({ key: k, url: signedUrls[k] || null, name: fileNameFromKey(k) })),
    ...pendingFiles.map((p) => ({ key: p.name, url: p.preview, name: p.name })),
  ];

  const zoneClass = [
    'pm-dropzone',
    isDragActive ? 'is-active' : '',
    invalid ? 'is-invalid' : '',
    disabled ? 'is-disabled' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div>
      <input
        ref={galleryInputRef}
        id={`${id}-gallery`}
        type="file"
        accept={ALLOWED_MEDIA_TYPES}
        multiple
        hidden
        onChange={handleNativePick}
        aria-hidden="true"
        tabIndex={-1}
      />
      <input
        ref={cameraInputRef}
        id={`${id}-camera`}
        type="file"
        accept="image/*,video/*"
        capture="environment"
        hidden
        onChange={handleNativePick}
        aria-hidden="true"
        tabIndex={-1}
      />

      <div {...getRootProps({ className: zoneClass })} aria-describedby={`${id}-help`}>
        <input {...getInputProps()} />
        <div className="pm-dropzone__row">
          <FeatherIcon icon="upload-cloud" size={22} color={isDragActive ? '#0e578e' : '#74788d'} />
          <div className="pm-dropzone__text" id={`${id}-help`}>
            {isDragActive ? 'Drop files here' : 'Drag and drop photos or videos here'}
            {required ? ' (at least one required)' : ''}
            <small>
              Images and videos up to 120 MB · {total}/{MAX_MEDIA_PER_SECTION} attached
            </small>
          </div>
          <div className="pm-dropzone__actions">
            <Button
              type="button"
              color="light"
              size="sm"
              disabled={disabled || atLimit}
              onClick={() => galleryInputRef.current && galleryInputRef.current.click()}
            >
              <FeatherIcon icon="image" size={15} className="me-1" />
              Choose files
            </Button>
            <Button
              type="button"
              color="light"
              size="sm"
              disabled={disabled || atLimit}
              onClick={() => cameraInputRef.current && cameraInputRef.current.click()}
            >
              <FeatherIcon icon="camera" size={15} className="me-1" />
              Camera
            </Button>
          </div>
        </div>

        {total > 0 && (
          <div className="pm-thumbs">
            {existingKeys.map((k, i) => (
              <span className="pm-thumb-wrap" key={k}>
                <MediaThumb
                  src={signedUrls[k] || null}
                  name={fileNameFromKey(k)}
                  kind={mediaKind(k)}
                  onOpen={() => setLightbox({ open: true, index: i })}
                />
                {onRemoveExisting && !disabled && (
                  <button
                    type="button"
                    className="pm-thumb-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveExisting(k);
                    }}
                    aria-label={`Remove ${fileNameFromKey(k)}`}
                    title="Remove"
                  >
                    <FeatherIcon icon="x" size={12} />
                  </button>
                )}
              </span>
            ))}
            {pendingFiles.map((p, i) => (
              <span className="pm-thumb-wrap" key={p.id}>
                <MediaThumb
                  src={p.preview}
                  name={p.name}
                  kind={p.kind}
                  pending
                  onOpen={() => setLightbox({ open: true, index: existingKeys.length + i })}
                />
                {!disabled && (
                  <button
                    type="button"
                    className="pm-thumb-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      removePending(p.id);
                    }}
                    aria-label={`Remove ${p.name}`}
                    title="Remove"
                  >
                    <FeatherIcon icon="x" size={12} />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
      </div>

      <MediaLightbox
        isOpen={lightbox.open}
        items={lightboxItems}
        index={lightbox.index}
        onIndexChange={(i) => setLightbox({ open: true, index: i })}
        onClose={() => setLightbox({ open: false, index: 0 })}
      />
    </div>
  );
};

export { MediaZone };
export default MediaZone;
